import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { type List } from 'immutable';

/** The three severity slots the design shows, most severe first. */
export type AlarmSeverity = 'highest' | 'high' | 'medium';

export const ALARM_SEVERITIES: AlarmSeverity[] = ['highest', 'high', 'medium'];

export interface AlarmsToTriageSegment {
  severity: AlarmSeverity;
  count: number;
  /** The client's own criticality label -- never hardcode these. */
  label: string;
  /** The `eht_alarms.critical` level the slot stands for. */
  criticality: number;
}

/** One icon set for every v2 alarm severity display: dashboard and device list. */
export const SEVERITY_ICON: Record<AlarmSeverity, typeof ErrorOutlineIcon> = {
  highest: ReportGmailerrorredIcon,
  high: WarningAmberIcon,
  medium: ErrorOutlineIcon,
};

export const SEVERITY_STYLE: Record<AlarmSeverity, { bar: string; icon: string }> = {
  highest: { bar: 'ds-bg-severity-highest', icon: 'ds-text-severity-highest' },
  high: { bar: 'ds-bg-severity-high', icon: 'ds-text-severity-high' },
  medium: { bar: 'ds-bg-severity-medium', icon: 'ds-text-severity-medium' },
};

/**
 * Maps the client's three highest criticality levels above 0 onto the severity
 * slots, most severe first, labelled with the client's own wording.
 *
 * @param {Record<string, number>} byCriticality alarm counts keyed by criticality level
 * @param {List} criticalityLabels the client's criticality labels
 * @returns {AlarmsToTriageSegment[]} one entry per slot, including zero counts
 */
export function toSeverityCounts (byCriticality: Record<string, number>, criticalityLabels: List<string>): AlarmsToTriageSegment[] {
  return ALARM_SEVERITIES.map((severity, idx) => {
    const criticality = criticalityLabels.size - 1 - idx;
    if (criticality < 1) return null;

    return {
      severity,
      count: byCriticality[criticality] || 0,
      label: criticalityLabels.get(criticality) || `${criticality}`,
      criticality,
    };
  }).filter(Boolean) as AlarmsToTriageSegment[];
}
